import {
  DeleteOutlined,
  MessageOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useSendout } from '@features/SendoutCreationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { skipToken } from '@reduxjs/toolkit/query';
import { Button, type InputRef, type MenuProps, Space } from 'antd';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useDeleteClientsMutation,
  useLazyGetClientsSearchResultQuery,
} from '@app/api/Clients/api';
import {
  useGetAllTagsQuery,
  useRemoveClientsTagsMutation,
  useSetClientsTagsBySearchIdMutation,
  useSetClientsTagsMutation,
} from '@app/api/Tags';
import { MassActionsDropdown } from '@app/components';
import type { MassActionType } from '@app/components/Modals/MassActionTypeModal/types';
import { FRONT_WARNING, SUCCESS } from '@app/constants';
import {
  RIGHTS_CLIENTS,
  RIGHTS_IS_CREATE,
  RIGHTS_IS_EDIT,
  RIGHTS_IS_EXPORT,
  RIGHTS_IS_ON,
} from '@app/constants/accessRights';
import { MOBILE } from '@app/constants/devicesWidth';
import { initialFilterGroups } from '@app/data';
import {
  useAccessRights,
  useClientsAccessRights,
  useContentWidth,
  useRightsToSendouts,
  useUserPrint,
  useWindowSize,
} from '@app/hooks';
import { ClientFiltersContext, NotificationContext } from '@app/providers';
import { useDeleteClientsBySearchIdMutation } from '@app/services/rtkQuery/Clients/api';
import { toTagItems } from '@app/shared/utils';
import type { NClients } from '@app/types/Clients';
import { changeStandardPaginationUi } from '@app/utils';

import { initialFiltersConfig } from '@pages/Clients/ClientsList/data/initialFiltersConfig';
import { useNewClientsListContext } from '@pages/Clients/NewClientsList/context/useNewClientsListContext';

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

export const useClientsList = () => {
  const navigate = useNavigate();
  const { hasUserRightForModuleView } = useAccessRights(
    RIGHTS_CLIENTS,
    RIGHTS_IS_ON
  );
  const {
    pageNumber,
    setPageNumber,
    clientsData,
    serverFilter,
    refetchClientsDataHandler,
    refetchClientsFilterDataHandler,
    setServerFilter,
    setClientsData,
  } = useNewClientsListContext();
  const { innerWidth } = useWindowSize();
  const { contentWidth } = useContentWidth();
  const [areFiltersOpened, setFiltersOpening] = useState<boolean>(
    innerWidth > 834
  );
  const [isMassActionTypeModalOpen, setIsMassActionTypeModalOpen] =
    useState(false);
  const [action, setAction] = useState<
    'addTags' | 'removeTags' | 'delete' | 'sendMessage' | null
  >(null);

  const [setClientsTags, { isLoading: isSettingClientsTags }] =
    useSetClientsTagsMutation();
  const [removeClientsTags, { isLoading: isRemovingClientsTags }] =
    useRemoveClientsTagsMutation();
  const [setTagsToAllClients, { isLoading: isSettingAllClientsTags }] =
    useSetClientsTagsBySearchIdMutation();

  const [actionType, setActionType] = useState<MassActionType | null>(null);
  // Группа поиска реестра — это Id последнего результата поиска.
  // SearchIdContext на этой странице никем не заполняется, поэтому
  // в рассылку уходило "null" вместо id (issue/11328).
  const searchId = clientsData?.Id ?? null;
  const searchIdRef = useRef(searchId);
  searchIdRef.current = searchId;
  const getSearchId = useCallback(
    () =>
      searchIdRef.current !== null ? String(searchIdRef.current) : undefined,
    []
  );
  const { activeBranchId } = useUserPrint();
  const [loader, setLoader] = useState(false);
  const searchTermRef = useRef<InputRef | null>(null);
  const downloadButtonRef = useRef();
  const [fileString] = useState('');
  const [totalRowsCount, setTotalRowsCount] = useState(0);
  const [isArchivingModalOpen, setIsArchivingModalOpen] = useState(false);
  const { showNotification } = useContext(NotificationContext);
  const [searchQueryError, setSearchQueryError] = useState('');
  const [isStartedPackData, setIsStartedPackData] = useState(false);
  const [showMassActionTypeLoading, setShowMassActionTypeLoading] =
    useState(false);

  const { hasUserRight: hasRightToCreate } = useAccessRights(
    RIGHTS_CLIENTS,
    RIGHTS_IS_CREATE
  );
  const { hasUserRight: hasRightToEdit } = useAccessRights(
    RIGHTS_CLIENTS,
    RIGHTS_IS_EDIT
  );
  const { hasUserRight: hasRightToExport } = useAccessRights(
    RIGHTS_CLIENTS,
    RIGHTS_IS_EXPORT
  );
  const { hasUserRightToDelete, hasUserRightToCommunicate } =
    useClientsAccessRights();
  const { isAccessToSendMessageFromClients } = useRightsToSendouts();

  const [tagsModal, setTagsModal] = useState(false);
  const [selectedTagsIds, setSelectedTagsIds] = useState<number[]>([]);
  const {
    serverFilters,
    tagsStorageKey,
    defaultTagIncludingStatuses,
    searchTerm,
    setSearchTerm,
    setFilters,
    setServerFilters,
    setFilterGroups,
    setTagIncludingStatuses,
  } = useContext(ClientFiltersContext);
  const [isResetModal, setResetModal] = useState(false);
  const isMobile = innerWidth <= MOBILE;
  const { openSendMessageModal, renderSendMessageModal } = useSendout();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const [deleteAllClients] = useDeleteClientsBySearchIdMutation();
  const [deleteClients] = useDeleteClientsMutation();
  const [getClientsSearchResult] = useLazyGetClientsSearchResultQuery();
  const tagsLoader =
    isSettingClientsTags || isRemovingClientsTags || isSettingAllClientsTags;

  const { data: tagsData, isFetching: isTagsLoading } = useGetAllTagsQuery(
    tagsModal && activeBranchId ? { ActiveBranchId: activeBranchId } : skipToken
  );

  const availableTags = useMemo(() => toTagItems(tagsData), [tagsData]);

  const onOpenMassActionTypeModal = useCallback(() => {
    setIsMassActionTypeModalOpen(true);
  }, []);

  const MassActionButton = useMemo(() => {
    const items: MenuProps['items'] = [
      ...(isAccessToSendMessageFromClients
        ? [
            {
              key: '1',
              label: (
                <Space
                  onClick={() => {
                    setAction('sendMessage');
                    onOpenMassActionTypeModal();
                  }}
                >
                  <MessageOutlined />
                  Отправить сообщение
                </Space>
              ),
            },
          ]
        : []),
      {
        key: '2',
        label: (
          <Space
            onClick={() => {
              setAction('addTags');
              onOpenMassActionTypeModal();
            }}
          >
            <TagOutlined />
            Добавить тег
          </Space>
        ),
      },
      {
        key: '2.1',
        disabled: selectedRowKeys.length === 0,
        onClick: () => {
          if (selectedRowKeys.length === 0) return;

          setSelectedTagsIds([]);
          setAction('removeTags');
          setTagsModal(true);
        },
        label: (
          <Space>
            <TagOutlined />
            Убрать теги
          </Space>
        ),
      },
      ...(hasUserRightToDelete
        ? [
            {
              key: '3',
              label: (
                <Space
                  onClick={() => {
                    setAction('delete');
                    onOpenMassActionTypeModal();
                  }}
                >
                  <DeleteOutlined />
                  Удалить из базы
                </Space>
              ),
            },
          ]
        : []),
    ];

    return (
      <div className="mass-actions">
        <MassActionsDropdown
          items={items}
          data={clientsData?.Clients || []}
          selectionCount={selectedRowKeys.length || 0}
        />
      </div>
    );
  }, [
    isAccessToSendMessageFromClients,
    hasUserRightToDelete,
    clientsData?.Clients,
    selectedRowKeys.length,
    onOpenMassActionTypeModal,
    setTagsModal,
    setSelectedTagsIds,
  ]);

  useEffect(() => {
    changeStandardPaginationUi({
      isMobile,
      actionButton: MassActionButton,
    });
  }, [MassActionButton, isMobile]);

  const onCloseMassActionTypeModal = useCallback(() => {
    setIsMassActionTypeModalOpen(false);
  }, []);

  const onConfirmMassActionType = useCallback(
    (type: MassActionType) => {
      setActionType(type);
      switch (action) {
        case 'addTags':
          setSelectedTagsIds([]);
          setTagsModal(true);
          break;
        case 'delete':
          setIsArchivingModalOpen(true);
          break;
        case 'sendMessage':
          openSendMessageModal?.(
            {
              usage: 'clients',
              actionType: type,
              activeBranchId: activeBranchId ?? 1,
              selectedIds:
                (selectedRowKeys as string[]).filter(Boolean).map(Number) ?? [],
              searchId: searchId !== null ? String(searchId) : undefined,
              getSearchId,
            },
            () => {
              setSelectedRowKeys([]);
            }
          );
          break;
      }
    },
    [
      action,
      activeBranchId,
      selectedRowKeys,
      openSendMessageModal,
      searchId,
      getSearchId,
    ]
  );

  const resetAll = useCallback(() => {
    if (searchTermRef.current && searchTermRef.current.input) {
      searchTermRef.current.input.value = '';
    }

    setSearchTerm('');
    setTotalRowsCount(0);

    setFilters(initialFiltersConfig);
    setServerFilters(initialFiltersConfig);
    setFilterGroups(initialFilterGroups);
    setTagIncludingStatuses(defaultTagIncludingStatuses);

    setTagIncludingStatuses(defaultTagIncludingStatuses);
    sessionStorage.setItem(
      tagsStorageKey,
      JSON.stringify(defaultTagIncludingStatuses)
    );

    setResetModal(false);
  }, [
    defaultTagIncludingStatuses,
    tagsStorageKey,
    setSearchTerm,
    setFilters,
    setServerFilters,
    setFilterGroups,
    setTagIncludingStatuses,
  ]);

  const deleteRows = useCallback(async () => {
    const selectionIdsAsNumber = selectedRowKeys.map((item) => Number(item));

    setIsArchivingModalOpen(false);

    try {
      if (actionType === 'all') {
        setShowMassActionTypeLoading(true);

        if (!activeBranchId)
          return showNotification(
            'Идентификатор активного филиала не найден',
            FRONT_WARNING
          );

        if (!searchId)
          return showNotification(
            'Идентификатор поискового запроса не найден',
            FRONT_WARNING
          );

        await deleteAllClients({
          ActiveBranchId: activeBranchId,
          searchId,
        }).unwrap();
      } else {
        if (!activeBranchId)
          return showNotification(
            'Идентификатор активного филиала не найден',
            FRONT_WARNING
          );

        await deleteClients({
          ActiveBranchId: activeBranchId,
          data: selectionIdsAsNumber,
        }).unwrap();
      }
      setPageNumber(1);

      refetchClientsFilterDataHandler();

      showNotification(
        'Клиенты удалятся из базы в течение нескольких минут',
        SUCCESS
      );
    } catch (error) {
      showNotification(error as Error);
    } finally {
      setShowMassActionTypeLoading(false);
      setSelectedRowKeys([]);
    }
  }, [
    actionType,
    activeBranchId,
    deleteAllClients,
    deleteClients,
    selectedRowKeys,
    showNotification,
    searchId,
    refetchClientsFilterDataHandler,
    setPageNumber,
  ]);

  const handlePageChange = async (page: number, pageSize: number) => {
    document.querySelector('.page-content')?.scrollTo(0, 0);

    const searchId = clientsData?.Id;
    if (searchId) {
      if (!activeBranchId)
        return showNotification(
          'Идентификатор активного филиала не найден',
          FRONT_WARNING
        );

      setLoader(true);
      setIsStartedPackData(false);

      try {
        const response = await getClientsSearchResult({
          ActiveBranchId: activeBranchId,
          searchId,
          page: pageSize === serverFilter.ClientsPerPage ? page : 1,
          rowsPerPage: pageSize,
        }).unwrap();
        setClientsData(response);
        setServerFilter((prev) =>
          prev.ClientsPerPage !== pageSize
            ? {
                ...prev,
                ClientsPerPage: pageSize,
              }
            : prev
        );
        setTotalRowsCount(response.CountClients || 0);
        setPageNumber(response.PageN);
      } catch (error) {
        showNotification(error as Error);
      } finally {
        setLoader(false);
      }
    }
  };

  const handleEdit = (id: NClients.ClientFull['Id']) =>
    navigate(`/clients/edit/?id=${id}&key=2`);

  const closeTagsModal = useCallback(() => {
    if (tagsLoader) return;

    setTagsModal(false);
    setSelectedTagsIds([]);
  }, [tagsLoader]);

  const addTags = useCallback(async () => {
    const selectionIdsAsNumber = selectedRowKeys
      .map((item) => Number(item))
      .filter(Number.isFinite);

    if (!activeBranchId) {
      showNotification(
        'Идентификатор активного филиала не найден',
        FRONT_WARNING
      );
      return;
    }

    if (selectedTagsIds.length === 0) {
      showNotification('Выберите хотя бы один тег', FRONT_WARNING);
      return;
    }

    if (actionType !== 'all' && selectionIdsAsNumber.length === 0) {
      showNotification('Не выбраны клиенты', FRONT_WARNING);
      return;
    }

    const allClientsTagsRequest =
      actionType === 'all' && searchId !== null
        ? {
            ActiveBranchId: activeBranchId,
            data: {
              SearchId: searchId,
              TagIds: selectedTagsIds,
            },
          }
        : null;

    if (actionType === 'all' && allClientsTagsRequest === null) {
      showNotification(
        'Идентификатор поискового запроса не найден',
        FRONT_WARNING
      );
      return;
    }

    try {
      if (allClientsTagsRequest) {
        setShowMassActionTypeLoading(true);

        await setTagsToAllClients(allClientsTagsRequest).unwrap();
      } else {
        await setClientsTags({
          ActiveBranchId: activeBranchId,
          data: {
            ClientIds: selectionIdsAsNumber,
            TagIds: selectedTagsIds,
          },
        }).unwrap();
      }

      showNotification('Теги успешно добавлены', SUCCESS);
      setTagsModal(false);
      setSelectedTagsIds([]);
      setSelectedRowKeys([]);
      await refetchClientsDataHandler();
    } catch (error) {
      showNotification(error);
    } finally {
      setShowMassActionTypeLoading(false);
    }
  }, [
    activeBranchId,
    showNotification,
    selectedTagsIds,
    selectedRowKeys,
    actionType,
    refetchClientsDataHandler,
    searchId,
    setClientsTags,
    setTagsToAllClients,
  ]);

  // Removing tags is supported only for explicitly selected clients: the
  // backend exposes RemoveClientsTags (by ClientIds) without a by-search-id
  // variant, so this action does not offer the "active filter" mode.
  const removeTags = useCallback(async () => {
    const selectionIdsAsNumber = selectedRowKeys
      .map((item) => Number(item))
      .filter(Number.isFinite);

    if (!activeBranchId) {
      showNotification(
        'Идентификатор активного филиала не найден',
        FRONT_WARNING
      );
      return;
    }

    if (selectionIdsAsNumber.length === 0) {
      showNotification('Не выбраны клиенты', FRONT_WARNING);
      return;
    }

    if (selectedTagsIds.length === 0) {
      showNotification('Выберите хотя бы один тег', FRONT_WARNING);
      return;
    }

    try {
      await removeClientsTags({
        ActiveBranchId: activeBranchId,
        data: {
          ClientIds: selectionIdsAsNumber,
          TagIds: selectedTagsIds,
        },
      }).unwrap();

      showNotification('Теги успешно сняты', SUCCESS);
      setTagsModal(false);
      setSelectedTagsIds([]);
      setSelectedRowKeys([]);
      await refetchClientsDataHandler();
    } catch (error) {
      showNotification(error);
    }
  }, [
    activeBranchId,
    refetchClientsDataHandler,
    removeClientsTags,
    selectedRowKeys,
    selectedTagsIds,
    showNotification,
  ]);

  const memoizedFiltersOpening = useMemo(() => {
    const isDesktop = innerWidth > 834;

    if (isDesktop) {
      return true;
    } else {
      if (areFiltersOpened) {
        return true;
      } else {
        return false;
      }
    }
  }, [innerWidth, areFiltersOpened]);

  const memoizedFilterGroupButtons = useMemo(() => {
    return (
      <>
        {innerWidth <= 834 && (
          <>
            {areFiltersOpened ? (
              <Button
                className="filters-manager"
                block
                icon={
                  <FontAwesomeIcon
                    icon={'fa-solid fa-chevron-up' as IconProp}
                  />
                }
                onClick={() => setFiltersOpening(!areFiltersOpened)}
              >
                Скрыть фильтры
              </Button>
            ) : (
              <Button
                className="filters-manager"
                block
                type="primary"
                icon={
                  <FontAwesomeIcon
                    icon={'fa-solid fa-chevron-down' as IconProp}
                  />
                }
                onClick={() => setFiltersOpening(!areFiltersOpened)}
              >
                Показать фильтры
              </Button>
            )}
          </>
        )}
      </>
    );
  }, [areFiltersOpened, innerWidth]);

  const memoizedSearchFieldStyles = useMemo(() => {
    let width = '';
    let minWidth = '';

    if (innerWidth > 950) {
      width = '540px';
    } else if (innerWidth <= 950) {
      minWidth = '100%';
    } else {
      minWidth = '100%';
    }

    return {
      width,
      minWidth,
    };
  }, [innerWidth]);

  return {
    searchTerm,
    loader,
    rowSelection,
    ref: searchTermRef,
    searchId,
    downloadButtonRef,
    fileString,
    totalRowsCount,
    isArchivingModalOpen,
    selectionCount: selectedRowKeys.length || 0,
    hasRightToExport,
    hasRightToCreate,
    hasRightToEdit,
    filters: serverFilters,
    searchQueryError,
    tagsModal,
    tagsLoader,
    isTagsLoading,
    availableTags,
    selectedTagsIds,
    isResetModal,
    contentWidth,
    areFiltersOpened,
    memoizedFiltersOpening,
    memoizedFilterGroupButtons,
    memoizedSearchFieldStyles,
    hasUserRightForModuleView,
    innerWidth,
    setSelectedTagsIds,
    closeTagsModal,
    addTags,
    removeTags,
    setResetModal,
    setFiltersOpening,
    setFilters,
    navigate,
    handleEdit,
    handlePageChange,
    deleteRows,
    setIsArchivingModalOpen,
    setTotalRowsCount,
    resetAll,
    setSearchTerm,
    setSearchQueryError,
    showNotification,
    isMobile,
    MassActionButton,
    isStartedPackData,
    pageNumber,
    hasUserRightToCommunicate,
    isMassActionTypeModalOpen,
    onCloseMassActionTypeModal,
    onConfirmMassActionType,
    actionType,
    action,
    showMassActionTypeLoading,
    disabledSelectedItemsActions: !(selectedRowKeys.length || 0),
    renderSendMessageModal,
  };
};
